<template>
  <div>
    <div class="tw-px-4 tw-pt-4 tw-mb-4">
      <div class="tw-text-base tw-font-bold tw-text-sf-neutral tw-mb-2">
        {{ I18n.t('ledger_import.prepare_period.roll_forward_options') }}
      </div>
    </div>

    <div class="tw-px-4 tw-mb-4">
      <SfCheckbox
        :model-value="selectAllChecked"
        :disabled="!isPeriodSelected"
        @update:model-value="handleSelectAllChanged"
      >
        <template #right-side>
          <span v-dompurify-html="selectAllLabel" class="sf-checkbox__label" />
        </template>
      </SfCheckbox>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import useI18n from '@app/shared/vue/composables/use-I18n'
import { SfCheckbox } from '@silverfin/ui-components'
import { getMainAccountCheckboxes, getReconciliationWrappers } from '../utils'

interface TemplateCounts {
  totalTemplates: number
  workflows: number
}

const I18n = useI18n()

const selectAllChecked = ref(false)
const isPeriodSelected = ref(false)
const templateCounts = ref<TemplateCounts>({ totalTemplates: 0, workflows: 0 })
let isSelectAllInProgress = false

const selectAllLabel = computed(() => {
  if (!isPeriodSelected.value || templateCounts.value.totalTemplates === 0) {
    return I18n.t('ledger_import.prepare_period.select_all')
  }

  return I18n.t('ledger_import.prepare_period.select_all_with_count', {
    count: templateCounts.value.totalTemplates,
    workflows: templateCounts.value.workflows,
  })
})

const handleSelectAllChanged = (isChecked: boolean) => {
  selectAllChecked.value = isChecked
  isSelectAllInProgress = true

  getMainAccountCheckboxes().forEach((checkbox) => {
    checkbox.checked = isChecked
    checkbox.dispatchEvent(new Event('change', { bubbles: true }))
  })

  getReconciliationWrappers().forEach((wrapper) => {
    const input = wrapper.querySelector<HTMLInputElement>(
      'input[data-workflow-id]'
    )
    if (input) {
      if (isChecked && input.checked) {
        // if there are already checked boxes and we're checking all,
        // uncheck it first to ensure all children get selected
        wrapper.click()
        wrapper.click()
      } else if (input.checked !== isChecked) {
        wrapper.click()
      }
    }
  })

  isSelectAllInProgress = false
}

// ledger-copier.js fires this on every uncheck, including the ones the
// select-all itself causes: re-selecting an already checked workflow briefly
// unchecks it, and a deselect-all unchecks every account tile.
const deselectAll = () => {
  if (isSelectAllInProgress) return

  selectAllChecked.value = false
}

const updateCounts = (counts: TemplateCounts) => {
  templateCounts.value = counts
}

// count existing templates in the DOM to not send extra API requests
const countExistingTemplates = () => {
  const accountCheckboxes = document.querySelectorAll(
    '[data-behaviour="ledger-copier-accounts-list"] .card-body input[type="checkbox"]'
  ).length
  const reconciliationCheckboxes = document.querySelectorAll(
    '[data-behaviour="ledger-copier-workflow-list"] input[type="checkbox"]'
  ).length
  const reconciliationWorkflows = document.querySelectorAll(
    '[data-behaviour="ledger-copier-reconciliation-selector"]'
  ).length

  updateCounts({
    totalTemplates: accountCheckboxes + reconciliationCheckboxes,
    workflows: reconciliationWorkflows,
  })
}

const handlePeriodChange = () => {
  const sourceDropdown = document.querySelector<HTMLSelectElement>(
    '[name="ledger_copier[source_id]"]'
  )
  isPeriodSelected.value = !!(sourceDropdown && sourceDropdown.value)

  if (isPeriodSelected.value) {
    countExistingTemplates()
  } else {
    resetSelectAll()
  }
}

const resetSelectAll = () => {
  updateCounts({ totalTemplates: 0, workflows: 0 })
  selectAllChecked.value = false
}

onMounted(() => {
  handlePeriodChange()

  const sourceDropdown = document.querySelector<HTMLSelectElement>(
    '[name="ledger_copier[source_id]"]'
  )
  if (sourceDropdown) {
    sourceDropdown.addEventListener('change', resetSelectAll)
  }

  document.addEventListener(
    'ledger-copier:templates-loaded',
    countExistingTemplates
  )
  document.addEventListener('ledger-copier:deselect-all', deselectAll)
})

onUnmounted(() => {
  const sourceDropdown = document.querySelector<HTMLSelectElement>(
    '[name="ledger_copier[source_id]"]'
  )
  if (sourceDropdown) {
    sourceDropdown.removeEventListener('change', resetSelectAll)
  }

  document.removeEventListener(
    'ledger-copier:templates-loaded',
    countExistingTemplates
  )
  document.removeEventListener('ledger-copier:deselect-all', deselectAll)
})
</script>
